import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import { useAutoPreview } from "../AutoPreviewProvider/AutoPreviewContext";
import { getGameImage } from "../../../models/game";
import { Image } from "@onmo/ui";
import { Game } from "@onmo/graphql/graphql";
import { useStyles } from "./GameCard.style";
import { useLongPress } from "../../../hooks/useLongPress";



type GameCardProps = {
  game: Game;
  id: string;
  onClick: () => void;
  gemsSlot?: React.ReactNode;
};

export const GameCard = memo((props: GameCardProps) => {
  const {
    game,
    id,
    onClick,
    gemsSlot,
  } = props;

  const {
    activePreviewId,
    setActivePreviewId,
    registerCard,
    unregisterCard,
    removeBrokenId,
    onPreviewEnded,
  } = useAutoPreview();

  const classes = useStyles();
  const [shouldMountVideo, setShouldMountVideo] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const containerRef = useRef<HTMLDivElement>(null); // Local ref to the element

  const { webp, webp2x, webp3x } = getGameImage("small", game);
  const videoSrc = `${import.meta.env.VITE_CDN_PATH}assets/games_v2/${game.id}/game.mp4`;

  const isActive = activePreviewId === id;
  const canPreview = !!videoSrc && isActive;

  const handleLongPress = useCallback(() => {
    setActivePreviewId(id);
  }, [id, setActivePreviewId]);

  const handlePressEnd = useCallback(() => {
    if (activePreviewId === id) {
      setActivePreviewId(null);
    }
  }, [id, activePreviewId, setActivePreviewId]);

  const { wasLongPressedRef } = useLongPress(containerRef, {
    onLongPress: handleLongPress,
    onPressEnd: handlePressEnd
  });

  useEffect(() => {
    const el = containerRef.current;
    if (el && id) {
      registerCard(id, el);
    }

    return () => {
      if (id) unregisterCard(id);
    };
  }, [id, registerCard, unregisterCard]);

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      if (wasLongPressedRef.current) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      onClick();
    },
    [onClick, wasLongPressedRef]
  );

  useEffect(() => {
    if (canPreview) {
      setShouldMountVideo(true);
      const timer = setTimeout(() => {
        const v = videoRef.current;
        if (v) {
          v.muted = true;
          v.play().catch(() => { });
        }
      }, 0);
      return () => clearTimeout(timer);
    } else {
      setShouldMountVideo(false);
    }
  }, [canPreview]);

  const handleVideoError = () => {
    removeBrokenId(id);
  };

  return (
    <div
      ref={containerRef}
      className={classes.card}
      onClick={handleClick}
      style={{ position: 'relative', overflow: 'hidden' }}
    >
      <div className={classes.layer} />
      <Image
        src={webp || ""}
        src2x={webp2x}
        src3x={webp3x}
        key={game.id}
        className={classes.gameImg}
        showloading
      />
      {gemsSlot}

      {
        shouldMountVideo && videoSrc && (
          <div
            className={classes.videoWrapper}
            style={{ opacity: canPreview ? 1 : 0 }}
          >
            <video
              ref={videoRef}
              muted
              playsInline
              preload="auto"
              poster={webp || ""}
              onEnded={() => onPreviewEnded?.(id)}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                pointerEvents: "none",
                borderRadius: "inherit",
              }}
              onError={handleVideoError}
            >
              <source src={videoSrc} type="video/mp4" />
            </video>
          </div>
        )
      }
    </div >
  );
});

GameCard.displayName = "GameCard";